import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChatCircleText, X, PaperPlaneTilt, Heart, Smiley, SmileyMeh, SmileySad } from "@phosphor-icons/react";

const SENTIMENTS = [
  { key: "love", label: "Love it", Icon: Heart },
  { key: "good", label: "Good", Icon: Smiley },
  { key: "confused", label: "Confused", Icon: SmileyMeh },
  { key: "frustrated", label: "Frustrated", Icon: SmileySad },
];

const APP_PREFIXES = ["/dashboard", "/review", "/portfolio", "/finds", "/roadmap", "/pricing", "/profitability", "/admin", "/community"];

export default function FeedbackWidget() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [sentiment, setSentiment] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const visible = !!user && APP_PREFIXES.some((p) => pathname.startsWith(p));
  if (!visible) return null;

  const submit = async () => {
    if (!sentiment) { toast.error("Pick how it's going first."); return; }
    setBusy(true);
    try {
      await api.post("/feedback", { sentiment, comment: comment.trim(), page: pathname });
      toast.success("Thanks — the team reads every single one. 🙏");
      setOpen(false);
      setSentiment("");
      setComment("");
    } catch (e) {
      toast.error("Couldn't send that — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-5 left-5 z-40" data-testid="feedback-widget">
      {open && (
        <div className="mb-3 w-[320px] bg-white border-2 border-[#0D1B2A] shadow-[8px_8px_0_0_rgba(10,10,10,0.9)] p-5" data-testid="feedback-panel">
          <div className="flex items-center justify-between mb-3">
            <p className="font-serif-display text-lg font-bold">How are we doing?</p>
            <button onClick={() => setOpen(false)} data-testid="feedback-close" className="text-[#8A867D] hover:text-[#0D1B2A]"><X size={18} weight="bold" /></button>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3" data-testid="feedback-sentiments">
            {SENTIMENTS.map((s) => (
              <button
                key={s.key}
                onClick={() => setSentiment(s.key)}
                data-testid={`feedback-sentiment-${s.key}`}
                className={`h-10 px-2 inline-flex items-center justify-center gap-1.5 border text-sm font-semibold transition-colors ${sentiment === s.key ? "bg-[#0D1B2A] text-[#FDFBF7] border-[#0D1B2A]" : "bg-white text-[#0D1B2A] border-[#0D1B2A]/25 hover:border-[#0D1B2A]"}`}
              >
                <s.Icon size={16} weight={sentiment === s.key ? "fill" : "regular"} className={sentiment === s.key ? "text-[#B47A3C]" : ""} /> {s.label}
              </button>
            ))}
          </div>
          <textarea
            data-testid="feedback-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="Anything specific? (optional)"
            className="w-full border border-[#0D1B2A]/25 rounded-none px-2.5 py-2 text-sm focus:border-[#0D1B2A] outline-none resize-none mb-3"
          />
          <button onClick={submit} disabled={busy} data-testid="feedback-submit" className="w-full h-10 bg-[#0D1B2A] text-[#FDFBF7] rounded-none text-sm font-medium inline-flex items-center justify-center gap-1.5 hover:bg-black transition-colors disabled:opacity-60">
            {busy ? "Sending…" : "Send"} <PaperPlaneTilt size={14} weight="fill" />
          </button>
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        data-testid="feedback-open"
        aria-label="How are we doing?"
        title="How are we doing?"
        className="h-11 w-11 sm:w-auto sm:pl-3 sm:pr-4 bg-[#B47A3C] text-white rounded-full shadow-lg inline-flex items-center justify-center sm:gap-2 text-sm font-semibold hover:bg-[#9c6832] transition-colors active:scale-95"
      >
        <ChatCircleText size={18} weight="fill" /> <span className="hidden sm:inline">How are we doing?</span>
      </button>
    </div>
  );
}
